import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LegalModal from './legal/LegalPages';
import ContactForm from './ContactForm';

// Simplified Footer Component - Legal Focus with Discrete Admin Access
export const SimplifiedFooter = ({ onAdminAccess }) => {
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [currentLegalPage, setCurrentLegalPage] = useState(null);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const { t } = useTranslation();

  const openLegalPage = (page) => {
    setCurrentLegalPage(page);
    setIsLegalModalOpen(true);
  };

  const closeLegalModal = () => {
    setIsLegalModalOpen(false);
    setCurrentLegalPage(null);
  };

  const openContactForm = () => {
    setIsContactFormOpen(true);
  };

  const closeContactForm = () => {
    setIsContactFormOpen(false);
  };

  const legalLinks = [
    { name: t('footer.terms'), page: 'terms' },
    { name: t('footer.privacy'), page: 'privacy' },
    { name: t('footer.cookies'), page: 'cookies' }
  ];

  return (
    <>
      <footer className="bg-slate-900 text-white relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-purple-500 rounded-full blur-3xl translate-x-40 translate-y-40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          {/* Logo & Description */}
          <div className="text-center mb-12">
            <motion.div 
              className="flex flex-col justify-center items-center space-y-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              {/* Cloud Image */}
              <div className="relative">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 shadow-2xl overflow-hidden hover:bg-white/30 transition-all duration-300">
                  <img 
                    src="https://res.cloudinary.com/dogguvwef/image/upload/v1751438121/Parents_j7ah6m.png"
                    alt="123zzz Cloud"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* App Name */}
              <span className="text-3xl font-black bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                123zzz
              </span>
            </motion.div>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Legal Links Section */}
          <div className="flex flex-col items-center space-y-8 mb-12">
            <h3 className="text-xl font-bold text-center bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              {t('footer.legal_info')}
            </h3>
            
            <div className="flex flex-wrap justify-center gap-6">
              {legalLinks.map((link, index) => (
                <motion.button
                  key={index}
                  onClick={() => openLegalPage(link.page)}
                  className="group relative px-6 py-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="text-slate-300 group-hover:text-white transition-colors font-medium">
                    {link.name}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/10 group-hover:to-purple-500/10 rounded-2xl transition-all duration-300"></div>
                </motion.button>
              ))}
            </div>

            {/* Contact Support Button */}
            <motion.button
              onClick={openContactForm}
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-gradient-to-r hover:from-blue-500/30 hover:to-purple-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-white font-medium">
                Contact Support
              </span>
            </motion.button>
          </div>
          
          {/* Bottom Copyright */}
          <div className="border-t border-slate-800 pt-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <p className="text-slate-500 text-sm">
                © {new Date().getFullYear()} 123zzz. {t('footer.copyright')}
              </p>
              
              {/* Discrete Admin Link */}
              <div className="mt-4">
                <button
                  onClick={onAdminAccess}
                  className="text-xs text-slate-600 hover:text-slate-400 transition-colors duration-300 opacity-50 hover:opacity-100"
                  title="Admin Access"
                >
                  ⚙️
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      <ContactForm 
        isOpen={isContactFormOpen} 
        onClose={closeContactForm} 
      />

      {/* Legal Modal */}
      <LegalModal 
        isOpen={isLegalModalOpen} 
        onClose={closeLegalModal} 
        page={currentLegalPage} 
      />
    </>
  );
};

export default SimplifiedFooter;